<script setup lang="ts">
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import { copyToClipboard, openInNewTab } from "../../utils/GeneralUtils.ts";
import { useI18n } from "vue-i18n";
import { computed, onMounted, ref, watch } from "vue";
import QRCode from "qrcode";

const { t } = useI18n();

const props = defineProps({
    url: {
        type: String,
        required: true,
    },
    showLabel: {
        type: Boolean,
        default: true,
    },
    /**
     * Value the QR code should encode. Defaults to {@link url}. Callers pass a short link here so the
     * QR encodes the shorter, easier-to-type URL while the displayed link stays the full one.
     */
    qrUrl: {
        type: String,
        default: undefined,
    },
});

const qrCodeDataUrl = ref<string | null>(null);
const qrDialogVisible = ref(false);

/** The URL encoded in the QR code — the explicit qrUrl if given, otherwise the displayed url. */
const encodedUrl = computed(() => props.qrUrl || props.url);

function downloadQRCode() {
    if (qrCodeDataUrl.value === null) return;
    const link = document.createElement("a");
    link.download = "qr.png";
    link.href = qrCodeDataUrl.value;
    link.click();
}

function generateQrCode(value: string) {
    QRCode.toDataURL(value, { width: 200 }, (err, url) => {
        if (err) {
            return;
        }

        qrCodeDataUrl.value = url;
    });
}

onMounted(() => generateQrCode(encodedUrl.value));
// Regenerate when the encoded URL changes (e.g. a short link is created, renamed or deleted).
watch(encodedUrl, (value) => generateQrCode(value));
</script>

<template>
    <div class="input-container">
        <label v-if="props.showLabel" for="questionnaire-link_input_direct-link" class="link-label">
            {{ t("project-details.direct-link.questionnaire-link") }}
        </label>
        <div class="input-field flex items-start">
            <InputText id="questionnaire-link_input_direct-link" class="custom mr-1 w-7/12 md:w-7/12" :value="url" disabled />
            <Button
                severity="secondary"
                class="mr-1 bg-persolog-blue text-white"
                icon="pi pi-copy"
                @click="copyToClipboard(url as string)"
                v-tooltip.top="{ value: t('project-details.direct-link.copy'), showDelay: 500 }"
            ></Button>
            <Button
                severity="secondary"
                class="mr-1 bg-persolog-blue text-white"
                icon="pi pi-external-link"
                @click="openInNewTab(url as string)"
                v-tooltip.top="{ value: t('project-details.direct-link.open'), showDelay: 500 }"
            ></Button>
            <Button
                id="questionnaire-link_btn_qr"
                severity="secondary"
                v-tooltip.top="{ value: t('project-configuration.directLink.QRCodeTooltip'), showDelay: 500 }"
                class="bg-persolog-blue text-white"
                icon="pi pi-qrcode"
                @click="qrDialogVisible = true"
                :aria-label="t('accessibility.buttons.showQRCode')">
            </Button>
            <slot name="after-actions" />
            <Dialog
                v-model:visible="qrDialogVisible"
                modal
                :draggable="false"
                :header="t('accessibility.images.qrCode')"
                :style="{ width: '26rem' }"
            >
                <div class="flex flex-col gap-6">
                    <div>
                        <div class="qr-code-container">
                            <img :src="qrCodeDataUrl || ''" :alt="t('accessibility.images.qrCode')" />
                        </div>
                        <div class="flex justify-end">
                            <Button text icon="pi pi-download" @click="downloadQRCode" :aria-label="t('accessibility.buttons.downloadQRCode')" />
                        </div>
                    </div>
                    <slot name="qr-extra" />
                </div>
            </Dialog>
        </div>
    </div>
</template>

<style>
.input-container {
    display: flex !important;
    flex-direction: column !important;
    align-items: left;

    .input-field {
        flex-direction: row !important;
    }
}

.link-label {
    font-size: 12px;
    color: #7e868d;
}

.qr-code-container {
    margin-top: 10px;
    display: flex;
    justify-content: center;
}

.qr-code-container img {
    width: 200px;
    height: 200px;
}
</style>
